/* Decorative only: alternate the existing dark background circles.
 * No layout, input, navigation, request or commerce state is changed. */
(function () {
  'use strict';
  if (window.ZinVoltDarkSurfaces) {
    window.ZinVoltDarkSurfaces.refresh();
    return;
  }
  const SURFACES = "#MainContent :is(.zv-about-hero, .zv-about-rating, .zv-about-video, .zv-about-final, .zv-final__panel, .zv-why__visual, .zv-installation__panel, .zv-cycle__panel, .zv-flow-panel, .zvc-intro, .zvc-app, .zvc-installation__panel, .zvc-capacity-card, .p1 .p1-advice__viewer, .zvcp__panel, .zv-core-choice__result, .zv-app-handleiding__hero, .zv-app-handleiding__toc, .zv-energy__toc, .zv-app-handleiding__activation, .zv-app-handleiding__final-cta, .zv-app-handleiding__mobile-jump, .zv-restart__bluetooth, .zv-products-hero, .zv-products-help, .zv-contact-hero, .zv-contact-help-card, .zv-review-hero, .b2b-hero, .b2b-access, .zv-blog-hero, .zv-article-hero, .zv-legal-hero, .shopify-policy__title, .zv-legal-help, .zv-search-hero, .zv-404-card, .zv-blog-image-placeholder, .zv-home-blog__placeholder, .zv-track__hero, .zv-track__direct-loader, .zv-track__status-card, .zv-track__info-card--dark, .zv-track__contact, .zv-sc__rail, .main-cart .page-header, .zinvolt-choose-head, .zv-guide__header, .zv-tech-check__box, .zv-calibration-check__box, .zv-multi-base-link__card, .zv-prerequisites__panel, .zv-attention-block__panel, .zv-stack-intro__header, .zv-stack-note, .zv-trouble__intro, .zv-trouble-footer, .zv-pdf-alert__inner, .zv-guide-workspace__rail, .zv-guide-contact-cta), body .zv-footer__frame, body .zv-advice-option--dark";
  const DIALOGS = 'dialog, [role="dialog"][aria-modal="true"], [data-zv-advies-modal]';
  const ATTRIBUTES = ['class', 'style', 'hidden', 'aria-hidden', 'open', 'inert'];
  const tracked = new Set();
  const dialogNames = new WeakMap();
  let dialogCount = 0;
  let frame = 0;
  let started = false;

  function schedule() {
    if (!started || frame) return;
    frame = window.requestAnimationFrame(update);
  }

  // Do not use viewport intersection or opacity here. Offscreen sections and
  // opacity-based reveal animations keep their place while a visitor scrolls.
  function participates(element) {
    if (!element.isConnected || element.closest('[hidden], [aria-hidden="true"]')) return false;
    if (!element.getClientRects().length) return false;
    const box = element.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
  }

  function groupFor(element) {
    // A portal/modal is an independent visual document. Its first dark card
    // starts at top-left regardless of the number of panels behind the modal.
    return element.closest(DIALOGS) || document;
  }

  function groupName(group) {
    if (group === document) return 'page';
    if (!dialogNames.has(group)) {
      dialogNames.set(group, group.id || 'dialog-' + (++dialogCount));
    }
    return dialogNames.get(group);
  }

  function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function clear(element) {
    ['data-zv-dark-direction', 'data-zv-dark-index', 'data-zv-dark-group'].forEach((name) => {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    });
  }

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

  function update() {
    frame = 0;
    // querySelectorAll returns each element once, in semantic document order.
    const elements = Array.from(document.querySelectorAll(SURFACES));
    const current = new Set(elements);
    tracked.forEach((element) => {
      if (current.has(element)) return;
      if (resizeObserver) resizeObserver.unobserve(element);
      tracked.delete(element);
      clear(element);
    });
    const counters = new Map();
    const assigned = new Map();
    elements.forEach((element) => {
      if (!tracked.has(element)) {
        tracked.add(element);
        if (resizeObserver) resizeObserver.observe(element);
      }
      if (!participates(element)) {
        clear(element);
        return;
      }
      const group = groupFor(element);
      const parent = element.parentElement && element.parentElement.closest(SURFACES);
      const parentAssignment = parent && assigned.get(parent);
      let index;
      if (parentAssignment && parentAssignment.group === group) {
        // A dark child inside a dark panel belongs to that composition. It
        // does not change the alternation of the next independent section.
        index = parentAssignment.index;
      } else {
        index = (counters.get(group) || 0) + 1;
        counters.set(group, index);
      }
      assigned.set(element, { group, index });
      setAttribute(element, 'data-zv-dark-direction', index % 2 ? 'top-left' : 'top-right');
      setAttribute(element, 'data-zv-dark-index', String(index));
      setAttribute(element, 'data-zv-dark-group', groupName(group));
    });
  }

  function containsSurface(node) {
    return node.nodeType === 1 && (node.matches(SURFACES) || node.querySelector(SURFACES));
  }

  function relevantMutation(mutation) {
    if (mutation.type === 'attributes') return containsSurface(mutation.target);
    // Animated values or copy changes within a panel do not require recounts.
    // ResizeObserver handles intrinsic size changes. Added/removed sections do.
    return Array.from(mutation.addedNodes).some(containsSurface)
      || Array.from(mutation.removedNodes).some((node) => {
        // A removed section no longer has #MainContent as an ancestor, so
        // its formerly matched descendants must be checked from our registry.
        return node.nodeType === 1 && Array.from(tracked).some((element) => node === element || node.contains(element));
      });
  }

  function start() {
    if (started) return;
    started = true;
    new MutationObserver((mutations) => {
      if (mutations.some(relevantMutation)) schedule();
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ATTRIBUTES });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pageshow', schedule);
    document.addEventListener('toggle', schedule, true);
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:section:select'].forEach((name) => {
      document.addEventListener(name, schedule);
    });
    update();
  }

  window.ZinVoltDarkSurfaces = { refresh: schedule };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
